import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';

import {
    fetchKeywordSuggestions,
    fetchLocationSuggestions,
    KeywordSuggestion,
} from './search-bar-service';
import { getGrowthbookClientSide } from '../../growthbook/init-growthbook';
import * as recentSearchesService from './recent-service/recent-searches-service';
import * as recentLocationsService from './recent-service/recent-locations-service';
import { ShellState, ShellUseDispatch } from '../../reducers/common';

export const toggleSearchModal = () => ({
    type: 'TOGGLE_SEARCH_MODAL',
});

export const setKeyword = (keyword: string) => ({
    type: 'SET_SEARCH_BAR_KEYWORD',
    keyword,
});

export const setInitialKeyword = (keyword: string) => ({
    type: 'SET_INITIAL_SEARCH_BAR_KEYWORD',
    keyword,
});

export const setKeywordSuggestions = (
    suggestions: KeywordSuggestion[],
    useRecentHistory = false
) => ({
    type: 'SET_SEARCH_BAR_KEYWORD_SUGGESTIONS',
    suggestions,
    useRecentHistory,
});

export const setKeywordSuggestionsLatency = (suggestionsLatency: number) => ({
    type: 'SET_SEARCH_BAR_KEYWORD_SUGGESTIONS_LATENCY',
    suggestionsLatency,
});

export const setLocationSuggestions = (
    suggestions: { name: string }[],
    useRecentHistory = false
) => {
    return {
        type: 'SET_SEARCH_BAR_LOCATION_SUGGESTIONS',
        suggestions,
        useRecentHistory,
    };
};

export const selectKeywordSuggestion =
    (
        suggestion: { name: string; category?: string; categoryDisplayName?: string },
        useRecentHistory: boolean
    ) =>
    (dispatch: ShellUseDispatch) => {
        if (useRecentHistory) {
            trackV2('KeywordHistorySelected', '', {}, true);
        } else {
            trackV2('KeywordSuggestionSelect', '', {}, true);
        }
        if (suggestion.category && suggestion.categoryDisplayName) {
            dispatch({
                type: 'SET_SEARCH_BAR_CATEGORY',
                category: {
                    label: suggestion.categoryDisplayName,
                    value: suggestion.category,
                },
            });
        }
        dispatch({
            type: 'SELECT_SEARCH_BAR_KEYWORD_SUGGESTION',
            suggestion,
        });
    };

export const noSuggestions = () => ({
    type: 'NO_KEYWORD_SUGGESTIONS',
});

export const removeSingleSearchFromHistory = (text: string) => (dispatch) => {
    trackGA4Event<GA4.RecentSearchCleared>({
        event: 'recent_search_cleared',
        searchTerm: text,
    });
    trackV2('KeywordHistoryCleared');

    recentSearchesService.removeSingleFromHistory(text);
    const suggestions = recentSearchesService.load();
    dispatch(setKeywordSuggestions(suggestions as KeywordSuggestion[], true));
};

export const removeSingleLocationFromHistory = (text: string) => async (dispatch: ShellUseDispatch, getState: () => ShellState) => {
    trackV2('LocationHistoryCleared');
    
    // Get the location type from current suggestions before deleting
    const state = getState();
    const currentSuggestion = state.searchBar.location.suggestions.find(
        s => s.name === text
    );
    
    // Track GA4 event for deleting location
    trackGA4Event<GA4.LocationDelete>({
        event: 'location_delete',
        locationType: normalizeLocationType(currentSuggestion?.type?.toLocaleUpperCase()),
        locationName: text,
        locationId: currentSuggestion?.id,
    });

    recentLocationsService.removeSingleFromHistory(text);
    
    // Reload from cache
    const suggestions = await recentLocationsService.loadWithApiSupport(state.userData);
    dispatch(setLocationSuggestions(suggestions, true));
};

export const clearAllSearchesFromHistory = () => (dispatch) => {
    trackGA4Event<GA4.RecentSearchClearAll>({ event: 'recent_search_clear_all' });
    trackV2('KeywordHistoryClearAll');

    recentSearchesService.clearAllFromHistory();
    dispatch(setKeywordSuggestions([], true));
};

export const clearAllLocationsFromHistory = () => async (dispatch: ShellUseDispatch, getState: () => ShellState) => {
    trackV2('LocationHistoryClearAll');

    recentLocationsService.clearAllFromHistory();
    
    // Reload from cache
    const state = getState();
    const suggestions = await recentLocationsService.loadWithApiSupport(state.userData);
    dispatch(setLocationSuggestions(suggestions, true));
};

export const loadRecentKeywordHistory = () => (dispatch: ShellUseDispatch) => {
    const suggestions = recentSearchesService.load();
    dispatch(setKeywordSuggestions(suggestions as KeywordSuggestion[], true));
};

let hasTrackedKeywordSuggestionShown = false;

export const setKeywordAndUpdateSuggestions = (value: string) => {
    return (dispatch: ShellUseDispatch) => {
        dispatch(setKeyword(value));
        if (value.length === 0) {
            loadRecentKeywordHistory();
        } else if (value.length < 2) {
            dispatch(setKeywordSuggestions([]));
        } else {
            const startFetchTime = performance.now();
            fetchKeywordSuggestions(value).then((data = []) => {
                const fetchTime = performance.now() - startFetchTime;

                dispatch(setKeywordSuggestionsLatency(fetchTime));

                const nonDominantCategoryProps = {
                    categoryDisplayName: 'All Categories',
                    category: 'all',
                    searchOptionsExactMatch: true,
                };

                if (!data.length) {
                    dispatch(
                        setKeywordSuggestions([
                            {
                                name: value,
                                highlight: value,
                                ...nonDominantCategoryProps,
                            },
                        ])
                    );
                } else {
                    const [suggestion, ...rest]: KeywordSuggestion[] = data;

                    if (!hasTrackedKeywordSuggestionShown) {
                        trackV2('KeywordSuggestionShown');
                        trackGA4Event<GA4.RecentSearchSuggestionsShown>({
                            event: 'recent_search_suggestions_shown',
                        });
                        hasTrackedKeywordSuggestionShown = true;
                    }

                    const withoutCategory =
                        getGrowthbookClientSide()?.getFeatureValue(
                            GrowthBookFeature.SUG_WITHOUT_CATEGORY,
                            'A'
                        ) === 'B';

                    if (withoutCategory) {
                        dispatch(setKeywordSuggestions(data));
                        return;
                    }

                    dispatch(
                        setKeywordSuggestions([
                            {
                                ...suggestion,
                                ...nonDominantCategoryProps,
                            } as KeywordSuggestion,
                            ...rest,
                        ])
                    );
                }
            });
        }
    };
};

export const setLocation = (location: string) => ({
    type: 'SET_SEARCH_BAR_LOCATION',
    location,
});

export const setInitialLocation = (location: string) => ({
    type: 'SET_INITIAL_SEARCH_BAR_LOCATION',
    location,
});

export const setG5SParams = (params: { busId: number; timeStamp: string; sign: string }) => ({
    type: 'SET_G5S_PARAMS',
    params,
});

export const setCategorySearch = (searchCategory: number) => ({
    type: 'SET_SEARCH_CATEGORY_ID',
    searchCategory,
});

export const selectLocationSuggestion =
    (suggestion: { name: string; type?: string; id?: string }, useRecentHistory: boolean) => (dispatch: ShellUseDispatch) => {
        if (useRecentHistory) {
            trackV2('LocationHistorySelected');
        } else {
            trackV2('LocationSuggestionSelect');
        }
        dispatch({
            type: 'SELECT_SEARCH_BAR_LOCATION_SUGGESTION',
            suggestion,
        });
    };

/**
 * Normalize location type from API to GA4 format
 */
const normalizeLocationType = (type?: string): 'LOCATION' | 'POSTCODE' | 'OUTCODE' => {
    if (type === 'POSTCODE' || type === 'OUTCODE') {
        return type;
    }
    // Default to 'LOCATION' for cities, areas, or undefined types
    return 'LOCATION';
};

export const loadRecentLocationHistory = () => async (dispatch: ShellUseDispatch, getState: () => ShellState) => {
    const state = getState();
    const userData = state.userData;

    // Only call the /get_recent_records API when the srp4 experiment is in variant B
    const isSrp4B = getGrowthbookClientSide()?.getFeatureValue(GrowthBookFeature.SRP_4, 'A') === 'B';

    const isSrpPage = typeof window !== 'undefined' && /^\/search\b/.test(window.location.pathname);
    const pathHasLocation = typeof window !== 'undefined' && (/\/uk\/?$/.test(window.location.pathname) || /\/uk\/(?!page\d)[^/]+/.test(window.location.pathname));

    // When gt_srp4_loc is already set (by submitSearch), it is the authoritative
    // preferred location. Skip clearing/overwriting cookies and prepopulating —
    // the server-side logic in page.ts already handled the correct location.
    const existingPreferredLoc = recentLocationsService.getPreferredLocationCookie();
    const hasAuthoritativeCookie = !!existingPreferredLoc;

    if (isSrp4B && !isSrpPage && !pathHasLocation && !hasAuthoritativeCookie) {
        recentLocationsService.clearLocationFromUserPrefCookie();
    }

    const currentLocation = state.searchBar.location;
    const needsPrepopulation = currentLocation.value === currentLocation.defaultLocation;

    // When prepopulation is needed, clear session cache so we get fresh API data.
    // Only on BRP pages — on SRP, the optimistic save() update must be preserved.
    if (isSrp4B && needsPrepopulation && userData?.userLoggedIn && !isSrpPage && !pathHasLocation) {
        recentLocationsService.forceApiRefresh();
    }

    const suggestions = await recentLocationsService.loadWithApiSupport(
        isSrp4B ? userData : null  // passing null skips the API call and falls back to localStorage
    );
    dispatch(setLocationSuggestions(suggestions, true));

    if (isSrp4B && suggestions.length > 0 && !isSrpPage && !pathHasLocation && !hasAuthoritativeCookie) {
        recentLocationsService.setPreferredLocationCookie(suggestions[0].name);
        recentLocationsService.updateLocationInUserPrefCookie(suggestions[0].name);
        recentLocationsService.cachePreferredLocation(suggestions[0].name);
    }

    if (needsPrepopulation && suggestions.length > 0 && !isSrpPage && !pathHasLocation && !hasAuthoritativeCookie) {
        dispatch(setInitialLocation(suggestions[0].name));
    }
};

let hasTrackedLocationSuggestionShown = false;

export const setLocationAndUpdateSuggestions = (value: string) => {
    return async (dispatch: ShellUseDispatch) => {
        dispatch(setLocation(value));
        if (value.length === 0) {
            // Load recent locations (will use API for logged-in users)
            await dispatch(loadRecentLocationHistory());
        } else if (value.length < 2) {
            dispatch(setLocationSuggestions([]));
        } else {
            fetchLocationSuggestions(value).then((suggestions) => {
                if (suggestions.length && !hasTrackedLocationSuggestionShown) {
                    trackV2('LocationSuggestionShown');
                    hasTrackedLocationSuggestionShown = true;
                }
                dispatch(setLocationSuggestions(suggestions));
            });
        }
    };
};

export const trackClickSearchBar =
    (termOrLocation: 'term' | 'location', optionsCount: number) =>
    (dispatch: ShellUseDispatch, getState: () => ShellState) => {
        const {
            searchBar: { userInteraction },
        } = getState();

        if (!userInteraction) {
            trackGA4Event<GA4.ClickSearchBar>({
                event: 'click_search_bar',
                search: {
                    fieldFirstClickedOn: termOrLocation,
                    recentHistoryShown: optionsCount > 0,
                },
            });
        }

        dispatch({ type: 'USER_HAS_INTERACTED_WITH_SEARCH_BAR' });
    };

export const hideSearchBar = () => ({
    type: 'HIDE_SEARCH_BAR',
});

/**
 * Refresh recent location history when user login status changes
 * This should be called after successful login to load recent locations from API
 */
export const refreshRecentLocationsOnLogin = () => async (dispatch: ShellUseDispatch, getState: () => ShellState) => {
    console.log('[Recent Locations] 🔄 User logged in, forcing API refresh');
    
    // Clear the session flag to allow API fetch after login
    recentLocationsService.forceApiRefresh();
    
    const state = getState();
    const { searchBar } = state;
    
    // Only refresh if the location field is empty (showing recent locations)
    if (searchBar.location.value.length === 0) {
        await dispatch(loadRecentLocationHistory());
    }
};
